import { useState } from "react";
import { View, ScrollView, StyleSheet, TextInput, Alert, Platform } from "react-native";
import { router } from "expo-router";
import { ThemedText } from "@/components/ThemedText";
import { Button } from "@/components/ui/Button";
import { useTheme } from "@/hooks/useTheme";
import { ModernCard } from "@/components/crew";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { DateInput } from "@/components/ui/DateInput";
import { InlineDropdown } from "@/components/ui/InlineDropdown";
import { ToggleSwitch } from "@/components/ui/ToggleSwitch";
import { PhotoCapture, PhotoFile, photoFileToBase64 } from "@/components/PhotoCapture";
import { supabase } from "@/lib/supabase-client";
const DISCREPANCY_TYPE_OPTIONS = [
  { label: "Missing Shift or Hours", value: "Missing Shift or Hours" },
  { label: "Incorrect Pay Rate or Position", value: "Incorrect Pay Rate or Position" },
  { label: "Missing or Incorrect Reimbursement", value: "Missing or Incorrect Reimbursement" },
  { label: "Pay Card or Banking Issue", value: "Pay Card or Banking Issue" },
  { label: "Didn't Receive a Paycheck", value: "Didn't Receive a Paycheck" },
  { label: "Other", value: "Other" },
];

const US_STATES = [
  { label: "Alabama", value: "AL" },
  { label: "Alaska", value: "AK" },
  { label: "Arizona", value: "AZ" },
  { label: "Arkansas", value: "AR" },
  { label: "California", value: "CA" },
  { label: "Colorado", value: "CO" },
  { label: "Connecticut", value: "CT" },
  { label: "Delaware", value: "DE" },
  { label: "Florida", value: "FL" },
  { label: "Georgia", value: "GA" },
  { label: "Hawaii", value: "HI" },
  { label: "Idaho", value: "ID" },
  { label: "Illinois", value: "IL" },
  { label: "Indiana", value: "IN" },
  { label: "Iowa", value: "IA" },
  { label: "Kansas", value: "KS" },
  { label: "Kentucky", value: "KY" },
  { label: "Louisiana", value: "LA" },
  { label: "Maine", value: "ME" },
  { label: "Maryland", value: "MD" },
  { label: "Massachusetts", value: "MA" },
  { label: "Michigan", value: "MI" },
  { label: "Minnesota", value: "MN" },
  { label: "Mississippi", value: "MS" },
  { label: "Missouri", value: "MO" },
  { label: "Montana", value: "MT" },
  { label: "Nebraska", value: "NE" },
  { label: "Nevada", value: "NV" },
  { label: "New Hampshire", value: "NH" },
  { label: "New Jersey", value: "NJ" },
  { label: "New Mexico", value: "NM" },
  { label: "New York", value: "NY" },
  { label: "North Carolina", value: "NC" },
  { label: "North Dakota", value: "ND" },
  { label: "Ohio", value: "OH" },
  { label: "Oklahoma", value: "OK" },
  { label: "Oregon", value: "OR" },
  { label: "Pennsylvania", value: "PA" },
  { label: "Rhode Island", value: "RI" },
  { label: "South Carolina", value: "SC" },
  { label: "South Dakota", value: "SD" },
  { label: "Tennessee", value: "TN" },
  { label: "Texas", value: "TX" },
  { label: "Utah", value: "UT" },
  { label: "Vermont", value: "VT" },
  { label: "Virginia", value: "VA" },
  { label: "Washington", value: "WA" },
  { label: "West Virginia", value: "WV" },
  { label: "Wisconsin", value: "WI" },
  { label: "Wyoming", value: "WY" },
  { label: "District of Columbia", value: "DC" },
];

export default function PayDiscrepancyScreen() {
  const { colors } = useTheme();
  const [isUnionPayroll, setIsUnionPayroll] = useState(false);
  const [unionNumber, setUnionNumber] = useState("");
  const [discrepancyType, setDiscrepancyType] = useState("");
  const [eventName, setEventName] = useState("");
  const [eventDate, setEventDate] = useState<Date>(new Date());
  const [state, setState] = useState("");
  const [paycheckDate, setPaycheckDate] = useState<Date>(new Date());
  const [waitedFiveToSeven, setWaitedFiveToSeven] = useState(false);
  const [explanation, setExplanation] = useState("");
  const [photos, setPhotos] = useState<PhotoFile[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const canSubmit = discrepancyType.length > 0 && eventName.trim().length > 0 && explanation.trim().length > 0;

  const handleSubmit = async () => {
    if (!canSubmit || isSubmitting) return;

    setIsSubmitting(true);
    try {
      const photoData = photos.length > 0 ? await Promise.all(photos.map(photoFileToBase64)) : undefined;

      const { data, error } = await supabase.functions.invoke("submit-pay-discrepancy", {
        body: {
          is_union_payroll: isUnionPayroll,
          union_number: isUnionPayroll ? unionNumber.trim() || undefined : undefined,
          discrepancy_type: discrepancyType,
          event_name: eventName.trim(),
          event_date: eventDate.toISOString(),
          state: state || undefined,
          paycheck_date: paycheckDate.toISOString(),
          waited_five_to_seven: waitedFiveToSeven,
          explanation: explanation.trim(),
          photos: photoData,
        },
      });

      if (error) {
        console.error("[PAY_DISCREPANCY] Submit error:", error);
        Alert.alert("Error", error.message || "Failed to submit pay discrepancy report.");
        return;
      }

      if (data?.success) {
        Alert.alert("Report Submitted", `Pay discrepancy report ${data.report_number} has been created.`, [
          { text: "OK", onPress: () => router.dismiss() },
        ]);
      } else {
        Alert.alert("Error", data?.error || "Unexpected response from server.");
      }
    } catch (e: any) {
      console.error("[PAY_DISCREPANCY] Submit exception:", e);
      Alert.alert("Error", e?.message || "Something went wrong.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ScrollView
      style={[styles.container, { backgroundColor: colors.backgroundSecondary }]}
      contentContainerStyle={styles.content}
      keyboardShouldPersistTaps="handled"
    >
      {/* 1. Union Payroll */}
      <ModernCard style={styles.card} margin={0}>
        <View style={styles.cardHeader}>
          <MaterialCommunityIcons name="account-hard-hat" size={24} color={colors.primary} />
          <ThemedText type="subtitle" style={{ marginLeft: 8 }}>
            Union Payroll
          </ThemedText>
        </View>
        <View style={styles.toggleRow}>
          <ThemedText style={styles.toggleLabel}>Is this for Union Payroll?</ThemedText>
          <ToggleSwitch value={isUnionPayroll} onValueChange={setIsUnionPayroll} />
        </View>
      </ModernCard>

      {/* 2. Union Number (conditional) */}
      {isUnionPayroll && (
        <ModernCard style={styles.card} margin={0}>
          <View style={styles.cardHeader}>
            <MaterialCommunityIcons name="pound" size={24} color={colors.primary} />
            <ThemedText type="subtitle" style={{ marginLeft: 8 }}>
              Union Number
            </ThemedText>
          </View>
          <TextInput
            style={[
              styles.searchInput,
              {
                color: colors.text,
                backgroundColor: colors.background,
                borderColor: colors.border,
              },
            ]}
            placeholder="Enter union number"
            placeholderTextColor={colors.placeholder}
            value={unionNumber}
            onChangeText={setUnionNumber}
          />
        </ModernCard>
      )}

      {/* 3. Discrepancy Type */}
      <ModernCard style={styles.card} margin={0}>
        <View style={styles.cardHeader}>
          <MaterialCommunityIcons name="alert-circle" size={24} color={colors.primary} />
          <ThemedText type="subtitle" style={{ marginLeft: 8 }}>
            Discrepancy Type
          </ThemedText>
        </View>
        <InlineDropdown
          options={DISCREPANCY_TYPE_OPTIONS}
          value={discrepancyType}
          onSelect={setDiscrepancyType}
          placeholder="Select discrepancy type..."
        />
      </ModernCard>

      {/* 4. Event Name */}
      <ModernCard style={styles.card} margin={0}>
        <View style={styles.cardHeader}>
          <MaterialCommunityIcons name="calendar-text" size={24} color={colors.primary} />
          <ThemedText type="subtitle" style={{ marginLeft: 8 }}>
            Event Name
          </ThemedText>
        </View>
        <TextInput
          style={[
            styles.searchInput,
            {
              color: colors.text,
              backgroundColor: colors.background,
              borderColor: colors.border,
            },
          ]}
          placeholder="Name of the event"
          placeholderTextColor={colors.placeholder}
          value={eventName}
          onChangeText={setEventName}
        />
      </ModernCard>

      {/* 5. Event Date */}
      <ModernCard style={styles.card} margin={0}>
        <View style={styles.cardHeader}>
          <MaterialCommunityIcons name="calendar" size={24} color={colors.primary} />
          <ThemedText type="subtitle" style={{ marginLeft: 8 }}>
            Event Date
          </ThemedText>
        </View>
        <DateInput value={eventDate} onChange={setEventDate} mode="date" label="Event Date" />
      </ModernCard>

      {/* 6. State */}
      <ModernCard style={styles.card} margin={0}>
        <View style={styles.cardHeader}>
          <MaterialCommunityIcons name="map-marker-outline" size={24} color={colors.primary} />
          <ThemedText type="subtitle" style={{ marginLeft: 8 }}>
            State
          </ThemedText>
        </View>
        <InlineDropdown options={US_STATES} value={state} onSelect={setState} placeholder="Select state..." />
      </ModernCard>

      {/* 7. Paycheck Date */}
      <ModernCard style={styles.card} margin={0}>
        <View style={styles.cardHeader}>
          <MaterialCommunityIcons name="cash" size={24} color={colors.primary} />
          <ThemedText type="subtitle" style={{ marginLeft: 8 }}>
            Paycheck Date
          </ThemedText>
        </View>
        <DateInput value={paycheckDate} onChange={setPaycheckDate} mode="date" label="Paycheck Date" />
      </ModernCard>

      {/* 8. Waited 5-7 Business Days */}
      <ModernCard style={styles.card} margin={0}>
        <View style={styles.cardHeader}>
          <MaterialCommunityIcons name="clock-outline" size={24} color={colors.primary} />
          <ThemedText type="subtitle" style={{ marginLeft: 8 }}>
            Wait Period
          </ThemedText>
        </View>
        <View style={styles.toggleRow}>
          <ThemedText style={styles.toggleLabel}>Have you waited 5-7 business days?</ThemedText>
          <ToggleSwitch value={waitedFiveToSeven} onValueChange={setWaitedFiveToSeven} />
        </View>
      </ModernCard>

      {/* 9. Explanation */}
      <ModernCard style={styles.card} margin={0}>
        <View style={styles.cardHeader}>
          <MaterialCommunityIcons name="text-box-outline" size={24} color={colors.primary} />
          <ThemedText type="subtitle" style={{ marginLeft: 8 }}>
            Explanation
          </ThemedText>
        </View>
        <TextInput
          style={[
            styles.notesInput,
            {
              color: colors.text,
              backgroundColor: colors.background,
              borderColor: colors.border,
            },
          ]}
          placeholder="Describe the pay discrepancy..."
          placeholderTextColor={colors.placeholder}
          value={explanation}
          onChangeText={setExplanation}
          multiline
          numberOfLines={4}
          textAlignVertical="top"
        />
      </ModernCard>

      {/* 10. Documentation Photos */}
      <ModernCard style={styles.card} margin={0}>
        <View style={styles.cardHeader}>
          <MaterialCommunityIcons name="camera" size={24} color={colors.primary} />
          <ThemedText type="subtitle" style={{ marginLeft: 8 }}>
            Documentation
          </ThemedText>
        </View>
        <PhotoCapture
          photos={photos}
          onPhotosChange={setPhotos}
          maxPhotos={10}
          placeholder="Tap to add photo"
          disabled={isSubmitting}
        />
      </ModernCard>

      {/* 11. Action Buttons */}
      <View style={styles.buttonRow}>
        <Button
          text="Cancel"
          icon="close"
          iconPosition="left"
          onPress={() => router.dismiss()}
          style={styles.button}
          enabled={!isSubmitting}
        />
        <Button
          text={isSubmitting ? "Submitting..." : "Submit Report"}
          icon="check"
          iconPosition="left"
          onPress={handleSubmit}
          style={[styles.button, { backgroundColor: colors.primary }]}
          textStyle={{ color: "white" }}
          enabled={canSubmit && !isSubmitting}
        />
      </View>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    padding: 16,
    paddingBottom: 32,
    gap: 16,
  },
  card: {},
  cardHeader: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 16,
  },
  toggleRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  toggleLabel: {
    fontSize: 14,
    fontWeight: "500",
    flex: 1,
    marginRight: 12,
  },
  searchInput: {
    height: 44,
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 12,
    fontSize: 14,
    ...(Platform.OS === "web" ? { outlineStyle: "none" as any } : {}),
  },
  notesInput: {
    borderWidth: 1,
    borderRadius: 8,
    padding: 12,
    fontSize: 14,
    minHeight: 120,
    ...(Platform.OS === "web" ? { outlineStyle: "none" as any } : {}),
  },
  buttonRow: {
    flexDirection: "row",
    gap: 12,
  },
  button: {
    flex: 1,
    height: 48,
  },
});
